<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 600));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #0f0524 0%, #1a0033 50%, #002a52 100%)",
      fontFamily:
        props.block.fontFamily || "'Orbitron', 'Bebas Neue', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.6}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 6}px`,
  };
});

const kanji = ["時", "刻", "未", "来", "夢", "光", "影", "霊"];
function pickKanji(i: number): string {
  return kanji[i % kanji.length];
}
</script>

<template>
  <div class="cd-anime" :style="containerStyle">
    <div class="cd-anime__lines" aria-hidden="true">
      <span
        v-for="i in 6"
        :key="i"
        :style="{ animationDelay: `${i * 0.4}s` }"
      />
    </div>

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-anime__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 130}ms`,
      }"
    >
      <span class="cd-anime__corner cd-anime__corner--tl" />
      <span class="cd-anime__corner cd-anime__corner--tr" />
      <span class="cd-anime__corner cd-anime__corner--bl" />
      <span class="cd-anime__corner cd-anime__corner--br" />
      <span class="cd-anime__kanji" aria-hidden="true">{{ pickKanji(i) }}</span>
      <span
        class="cd-anime__digit"
        :key="seg.value"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#ffffff',
        }"
      >
        {{ seg.value }}
      </span>
      <span
        class="cd-anime__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#ec4899',
        }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-anime {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  isolation: isolate;
}
.cd-anime__lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.cd-anime__lines span {
  position: absolute;
  left: -10%;
  right: -10%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(236, 72, 153, 0.7),
    transparent
  );
  animation: cd-anime-streak 5s linear infinite;
  opacity: 0.55;
}
.cd-anime__lines span:nth-child(1) {
  top: 12%;
}
.cd-anime__lines span:nth-child(2) {
  top: 28%;
}
.cd-anime__lines span:nth-child(3) {
  top: 45%;
}
.cd-anime__lines span:nth-child(4) {
  top: 60%;
}
.cd-anime__lines span:nth-child(5) {
  top: 76%;
}
.cd-anime__lines span:nth-child(6) {
  top: 90%;
}
@keyframes cd-anime-streak {
  0% {
    transform: translateX(-30%);
    opacity: 0;
  }
  20% {
    opacity: 0.8;
  }
  100% {
    transform: translateX(30%);
    opacity: 0;
  }
}
.cd-anime__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(236, 72, 153, 0.15),
    rgba(34, 211, 238, 0.15)
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(236, 72, 153, 0.5);
  box-shadow:
    0 0 24px rgba(236, 72, 153, 0.4),
    inset 0 0 24px rgba(34, 211, 238, 0.18);
  animation: cd-anime-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-anime-rise {
  from {
    opacity: 0;
    transform: translateY(20px) skewX(-3deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) skewX(0);
  }
}
.cd-anime__corner {
  position: absolute;
  width: 14px;
  height: 14px;
  border: 2px solid #22d3ee;
  filter: drop-shadow(0 0 4px #22d3ee);
}
.cd-anime__corner--tl {
  top: 4px;
  left: 4px;
  border-right: none;
  border-bottom: none;
}
.cd-anime__corner--tr {
  top: 4px;
  right: 4px;
  border-left: none;
  border-bottom: none;
}
.cd-anime__corner--bl {
  bottom: 4px;
  left: 4px;
  border-right: none;
  border-top: none;
}
.cd-anime__corner--br {
  bottom: 4px;
  right: 4px;
  border-left: none;
  border-top: none;
}
.cd-anime__kanji {
  position: absolute;
  font-size: 80px;
  color: rgba(236, 72, 153, 0.12);
  font-weight: 900;
  pointer-events: none;
  user-select: none;
  bottom: -10px;
  right: -6px;
  line-height: 1;
}
.cd-anime__digit {
  position: relative;
  font-weight: 700;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 10px rgba(236, 72, 153, 0.95),
    2px 0 0 rgba(34, 211, 238, 0.45),
    -2px 0 0 rgba(248, 113, 113, 0.35);
  display: inline-block;
  animation: cd-anime-glitch var(--cd-tick, 600ms) ease-out;
}
@keyframes cd-anime-glitch {
  0% {
    transform: translate(0, 0);
    opacity: 0;
  }
  20% {
    transform: translate(-2px, 1px);
    opacity: 0.6;
  }
  40% {
    transform: translate(2px, -1px);
  }
  60% {
    transform: translate(-1px, 0);
    opacity: 1;
  }
  100% {
    transform: translate(0, 0);
  }
}
.cd-anime__label {
  position: relative;
  margin-top: 6px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  text-shadow: 0 0 6px rgba(236, 72, 153, 0.6);
}
</style>
